/**
 * Resolve "check in with buddy" link from the viewer's preferred channel (Slack / Teams / email)
 * and the buddy's linked identities.
 */

export interface BuddyCheckInInput {
  preferredChannel: string | null | undefined
  slackTeamId: string | null | undefined
  buddySlackId: string | null | undefined
  buddyTeamsId: string | null | undefined
  buddyEmail: string | null | undefined
}

export interface BuddyCheckInAction {
  href: string
  label: string
  /** Use target="_blank" rel="noreferrer" */
  external: boolean
}

function normChannel(ch: string | null | undefined): string {
  const s = (ch ?? 'email').toLowerCase().trim()
  if (s === 'slack' || s === 'teams') return s
  return 'email'
}

/** Desktop Slack: opens DM / user in the correct workspace. */
export function slackAppUserDeepLink(slackTeamId: string, buddySlackUserId: string): string {
  return `slack://user?team=${encodeURIComponent(slackTeamId)}&id=${encodeURIComponent(buddySlackUserId)}`
}

export function teamsChatWithUserUrl(buddyEmail: string): string {
  return `https://teams.microsoft.com/l/chat/0/0?users=${encodeURIComponent(buddyEmail)}`
}

export function buddyCheckInMailto(buddyEmail: string): string {
  return `mailto:${buddyEmail}?subject=${encodeURIComponent('Accountability check-in')}`
}

export function resolveBuddyCheckInAction(input: BuddyCheckInInput): BuddyCheckInAction | null {
  const pref = normChannel(input.preferredChannel)
  const team = input.slackTeamId?.trim() || null
  const slack = input.buddySlackId?.trim() || null
  const email = input.buddyEmail?.trim() || null

  const canSlack = Boolean(team && slack)
  const canTeams = Boolean(email)

  const slackAction = (): BuddyCheckInAction | null =>
    canSlack && team && slack
      ? {
          href: slackAppUserDeepLink(team, slack),
          label: 'Message on Slack',
          external: true,
        }
      : null

  const teamsAction = (): BuddyCheckInAction | null =>
    canTeams && email
      ? {
          href: teamsChatWithUserUrl(email),
          label: 'Message on Teams',
          external: true,
        }
      : null

  const mailAction = (): BuddyCheckInAction | null =>
    email
      ? {
          href: buddyCheckInMailto(email),
          label: 'Send a check-in',
          external: false,
        }
      : null

  const order: (() => BuddyCheckInAction | null)[] =
    pref === 'slack'
      ? [slackAction, teamsAction, mailAction]
      : pref === 'teams'
        ? [teamsAction, slackAction, mailAction]
        : [mailAction, slackAction, teamsAction]

  for (const fn of order) {
    const a = fn()
    if (a) return a
  }
  return null
}
